import { useState, useCallback } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { getCachedRoles, setCachedRoles, clearRolesCache, type Role } from '@/lib/rolesCache';

export { clearRolesCache };
export type { Role };

export function useRoles() {
  const { makeAuthenticatedCall } = useAuthenticatedApi();
  const [roles, setRoles] = useState<Role[]>(getCachedRoles() ?? []);
  const [isLoading, setIsLoading] = useState(false);

  const fetchRoles = useCallback(async (force?: boolean) => {
    if (!force && getCachedRoles()) {
      setRoles(getCachedRoles()!);
      return;
    }

    setIsLoading(true);
    try {
      const result = await makeAuthenticatedCall<{ roles: Role[] }>('get-user-info-roles');
      if (result.data?.roles && Array.isArray(result.data.roles)) {
        const normalize = (r: any): Role => ({
          id: String(r.id ?? r.name ?? ''),
          label: r.label ?? r.name ?? String(r.id ?? ''),
          capabilities: r.capabilities && typeof r.capabilities === 'object' ? r.capabilities : undefined,
          children: Array.isArray(r.children) ? r.children.map(normalize) : [],
        });
        const normalized = result.data.roles.map(normalize);
        setCachedRoles(normalized);
        setRoles(normalized);
      }
    } catch (err) {
      console.error('Failed to fetch roles:', err);
    } finally {
      setIsLoading(false);
    }
  }, [makeAuthenticatedCall]);

  return { roles, isLoading, fetchRoles };
}
